<template>
  <DynamicModal
    :showModal="dialog"
    :title="isEditing ? 'Edit Task' : 'Add Task'"
    maxWidth="600px"
    @clickOutside="$emit('close')"
  >
    <v-container fluid slot="title-buttons" class="py-1">
      <v-row justify="end">
        <v-btn icon depressed color="primary" @click="$emit('close')">
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-row>
    </v-container>
    <v-container fluid slot="content">
        <v-form ref="form" class="mt-4">
          <v-text-field
            v-model="localTask.title"
            label="Title *"
            outlined
            dense
            :rules="[v => !!v || 'Title is required']"
          />
          <v-textarea
            v-model="localTask.description"
            label="Description"
            outlined
            dense
            rows="3"
          />
          <v-select
            v-model="localTask.defaultAssigneeRoleId"
            :items="roleOptions"
            label="Default Assignee Role"
            clearable
            outlined
            dense
          />
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="localTask.dueDateAnchorId"
                :items="dueDateAnchorOptions"
                item-text="text"
                item-value="value"
                label="Due Date Anchor *"
                outlined
                dense
                :rules="[v => !!v || 'Due date anchor is required']"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localTask.dueDateOffsetDays"
                label="Offset Days"
                type="number"
                outlined
                dense
                hint="Days after anchor date (negative = before)"
                persistent-hint
              />
            </v-col>
          </v-row>
          <v-select
            v-model="localTask.blockerTaskId"
            :items="blockerOptions"
            item-text="title"
            item-value="id"
            label="Blocked By (optional)"
            clearable
            outlined
            dense
          />
          <v-select
            v-model="localTask.taskTypeId"
            :items="taskTypeOptions"
            item-text="text"
            item-value="value"
            label="Task Type"
            outlined
            dense
          />
          <DocumentTemplateDropDownList
            v-if="localTask.taskTypeId === workflowTaskType.AutomatedDocument"
            :id="localTask.documentTemplateId"
            :isRequired="true"
            @templateIdChange="localTask.documentTemplateId = $event"
          />
          <WorkflowEmailTemplateDropDownList
            v-if="localTask.taskTypeId === workflowTaskType.AutomatedEmail"
            :id="localTask.workflowEmailTemplateId"
            :clientId="templateClientId"
            :isRequired="true"
            @templateIdChange="localTask.workflowEmailTemplateId = $event"
          />
          <template v-if="localTask.taskTypeId === workflowTaskType.AutomatedEmail">
            <div class="subtitle-2 mt-2 mb-1">Attach output from an earlier task</div>
            <v-row v-for="(attachment, index) in localTask.attachments" :key="index" align="center" dense>
              <v-col cols="10">
                <v-select
                  v-model="attachment.sourceTemplateTaskId"
                  :items="blockerOptions"
                  item-text="title"
                  item-value="id"
                  label="Source Task"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="2">
                <v-btn icon color="red" @click="removeAttachment(index)">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-btn outlined small color="primary" @click="addAttachment">
              <v-icon small left>add</v-icon>Add Attachment
            </v-btn>
            <v-alert v-if="attachmentSourceMismatch" type="warning" dense outlined class="mt-2">
              Set "Blocked By" above to the source task of the document this email attaches, so the
              email can't fire before its document is ready.
            </v-alert>
          </template>
        </v-form>
    </v-container>
    <v-container fluid slot="buttons" class="pb-0 mb-0">
      <v-row>
        <v-col cols="6">
          <v-btn
            color="primary"
            block
            @click="save"
          >
            Add
          </v-btn>
        </v-col>
        <v-col cols="6">
          <v-btn
            block
            depressed
            outlined
            @click="$emit('close')"
          >
            Cancel
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </DynamicModal>
</template>

<script>
import WorkflowEmailTemplateDropDownList from '@/components/workflowEmailTemplates/WorkflowEmailTemplateDropDownList'
import DocumentTemplateDropDownList from '@/components/documentTemplates/DocumentTemplateDropDownList'
import { workflowTaskType, WORKFLOW_TASK_TYPE_OPTIONS } from '@/lib/constants/workflowEmailStatuses'
import DynamicModal from '@/components/dialogs/DynamicModal'

export default {
  components: {
    WorkflowEmailTemplateDropDownList,
    DocumentTemplateDropDownList,
    DynamicModal
  },
  props: {
    task: {
      type: Object,
      default: () => ({})
    },
    templateId: {
      type: Number,
      required: true
    },
    templateClientId: {
      type: Number,
      default: null
    },
    existingTasks: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dialog: true,
      workflowTaskType,
      localTask: {
        taskTypeId: workflowTaskType.Manual,
        documentTemplateId: null,
        attachments: [],
        ...this.task
      },
      dueDateAnchorOptions: [
        { text: 'Assignment Start Date', value: 2 },
        { text: 'Deal Creation Date', value: 1 }
      ],
      roleOptions: [
        { text: 'Admin', value: 'ADMIN' },
        { text: 'Manager', value: 'MANAGER' },
        { text: 'Recruiter', value: 'RECRUITER' },
        { text: 'Sales Person', value: 'SALES_PERSON' },
        { text: 'Consultant', value: 'CONSULTANT' }
      ],
      taskTypeOptions: WORKFLOW_TASK_TYPE_OPTIONS.map(o => ({ text: o.name, value: o.id }))
    }
  },
  computed: {
    isEditing () {
      return !!this.task.id
    },
    blockerOptions () {
      return this.existingTasks.filter(t => t.id !== this.localTask.id)
    },
    attachmentSourceMismatch () {
      if (this.localTask.taskTypeId !== this.workflowTaskType.AutomatedEmail) return false
      return this.localTask.attachments.some(a => a.sourceTemplateTaskId && a.sourceTemplateTaskId !== this.localTask.blockerTaskId)
    }
  },
  watch: {
    'localTask.taskTypeId' (value) {
      if (value !== this.workflowTaskType.AutomatedEmail) {
        this.localTask.workflowEmailTemplateId = null
        this.localTask.attachments = []
      }
      if (value !== this.workflowTaskType.AutomatedDocument) {
        this.localTask.documentTemplateId = null
      }
    }
  },
  methods: {
    addAttachment () {
      this.localTask.attachments = [...this.localTask.attachments, { sourceTemplateTaskId: null, sortOrder: this.localTask.attachments.length }]
    },
    removeAttachment (index) {
      this.localTask.attachments = this.localTask.attachments.filter((a, i) => i !== index)
    },
    save () {
      if (!this.$refs.form.validate()) return

      const taskData = {
        ...this.localTask,
        workflowTemplateId: this.templateId
      }
      this.$emit('save', taskData)
    }
  }
}
</script>
